// ⚠️ GENERATED COPY — DO NOT EDIT.
// Source: shared/ui/journey/FactBlocks.tsx — edit there, then run: bash scripts/sync_shared_code.sh
// CI verifies these copies match the source (sync_shared_code.sh --check).
import React, { useRef, useState } from "react";
import { J, ensureJourneyKeyframes, motionOk } from "./journeyTheme";

export type FactRow = { key: string; label: string; value: string };
export type FactGroup = { key: string; title: string; accent: string; rows: FactRow[] };

// React 17 has no useId; a module counter gives each instance a stable prefix
// so input/label pairs stay unique when two FactBlocks share a page.
let instanceSeq = 0;

const pillBase: React.CSSProperties = {
  height: 34,
  padding: "0 14px",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
};

// The approved pill is 34px tall; the button around it is 44px so the touch
// target meets the minimum, with negative margin so row height is unchanged.
const hitArea: React.CSSProperties = {
  height: 44,
  margin: "-5px 0",
  padding: 0,
  border: 0,
  background: "transparent",
  cursor: "pointer",
  fontFamily: "inherit",
  display: "flex",
  alignItems: "center",
  flex: "0 0 auto",
};

export default function FactBlocks(props: {
  groups: FactGroup[];
  onChange: (rowKey: string, value: string) => void;
  editLabel?: string;
  saveLabel?: string;
  columns?: 1 | 2;
}) {
  const editLabel = props.editLabel || "Edit";
  const saveLabel = props.saveLabel || "Save";
  const idPrefix = useRef(`pj-fact-${++instanceSeq}`).current;
  // Keyframes go in before the first paint; injecting from an effect would
  // let "both" fill flash the cards visible and then hide them.
  const [animate] = useState(() => {
    ensureJourneyKeyframes();
    return motionOk();
  });
  const [editing, setEditing] = useState<{ rowKey: string; draft: string } | null>(null);

  const save = () => {
    if (!editing) return;
    props.onChange(editing.rowKey, editing.draft);
    setEditing(null);
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: props.columns === 2 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)",
        gap: 14,
        alignItems: "start",
      }}
    >
      {props.groups.map((group, gi) => (
        <section
          key={group.key}
          aria-label={group.title}
          style={{
            background: J.white,
            color: J.ink,
            borderRadius: J.radiusCard,
            padding: "14px 16px 6px",
            boxShadow: J.shadow,
            animation: animate ? "pj-fadeUp .45s ease both" : undefined,
            animationDelay: animate ? `${gi * 90}ms` : undefined,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div
              aria-hidden="true"
              style={{ width: 8, height: 8, borderRadius: 2, background: group.accent, flex: "0 0 8px" }}
            />
            <div
              style={{
                fontSize: 11,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: J.teal,
                fontWeight: 700,
              }}
            >
              {group.title}
            </div>
          </div>
          {group.rows.map((row) => {
            const isEditing = editing !== null && editing.rowKey === row.key;
            const inputId = `${idPrefix}-${group.key}-${row.key}`;
            return (
              <div
                key={row.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 0",
                  borderTop: `1px solid ${J.hairline}`,
                }}
              >
                <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                  {isEditing ? (
                    <>
                      <label htmlFor={inputId} style={{ fontSize: 12, color: J.muted }}>
                        {row.label}
                      </label>
                      <input
                        id={inputId}
                        type="text"
                        autoFocus
                        value={editing ? editing.draft : ""}
                        onChange={(e) => setEditing({ rowKey: row.key, draft: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            setEditing(null);
                          } else if (e.key === "Enter") {
                            e.preventDefault();
                            save();
                          }
                        }}
                        style={{
                          height: 36,
                          border: `1.5px solid ${J.teal}`,
                          borderRadius: 9,
                          padding: "0 10px",
                          fontSize: 15,
                          fontFamily: "inherit",
                          color: J.ink,
                          outline: "none",
                          width: "100%",
                          boxSizing: "border-box",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 12, color: J.muted }}>{row.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, overflowWrap: "anywhere" }}>{row.value}</div>
                    </>
                  )}
                </div>
                {isEditing ? (
                  <button type="button" onClick={save} aria-label={`${saveLabel} ${row.label}`} style={hitArea}>
                    <span style={{ ...pillBase, border: 0, background: J.teal, color: J.white }}>{saveLabel}</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing({ rowKey: row.key, draft: row.value })}
                    aria-label={`${editLabel} ${row.label}`}
                    style={hitArea}
                  >
                    <span
                      style={{
                        ...pillBase,
                        border: `1.5px solid ${J.border}`,
                        background: J.white,
                        color: J.ink,
                      }}
                    >
                      {editLabel}
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
